<template>
  <div class="rm rm-theme">
    <!-- Početna: izbor izveštaja -->
    <template v-if="!currentScreen">
      <div class="rm-home-header">
        <span v-if="restaurantName" class="rm-muted">{{ restaurantName }}</span>
        <h1 class="rm-home-header__title">{{ $t("report_mobile.title") }}</h1>
        <button
          v-if="views.length > 1"
          type="button"
          class="rm-view-switch"
          :aria-label="$t('view')"
          @click="filterSheet = true"
        >
          {{ currentViewName }}
          <v-icon size="18" color="#9a3412">mdi-chevron-down</v-icon>
        </button>
      </div>

      <div class="rm-chip-row rm-home-periods">
        <button
          v-for="preset in quickPresets"
          :key="preset.key"
          type="button"
          class="rm-chip rm-chip--lg"
          :class="{ 'rm-chip--active': activePresetKey === preset.key }"
          :aria-pressed="String(activePresetKey === preset.key)"
          @click="applyPeriod(preset)"
        >
          {{ presetChipLabel(preset) }}
        </button>
        <button
          v-if="!activeQuickPreset"
          type="button"
          class="rm-chip rm-chip--lg rm-chip--active rm-num"
          aria-pressed="true"
          @click="periodSheet = true"
        >
          {{ periodShort }}
        </button>
        <button
          type="button"
          class="rm-chip rm-chip--lg rm-chip--icon"
          :aria-label="$t('report_mobile.choose_period')"
          @click="periodSheet = true"
        >
          <v-icon size="20" color="#1c1917">mdi-calendar-blank-outline</v-icon>
        </button>
      </div>

      <div class="rm-home-groups">
        <section v-for="group in menuGroups" :key="group.key" class="rm-home-group">
          <h2 class="rm-section-title">{{ group.title }}</h2>
          <div class="rm-card">
            <button
              v-for="item in group.items"
              :key="item.key"
              type="button"
              class="rm-menu-row"
              @click="openScreen(item.key)"
            >
              <span class="rm-menu-row__icon">
                <v-icon size="20" color="#c2410c">{{ item.icon }}</v-icon>
              </span>
              <span class="rm-menu-row__text">
                <span class="rm-menu-row__title">{{ item.title }}</span>
                <span class="rm-muted">{{ item.subtitle }}</span>
              </span>
              <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
            </button>
          </div>
        </section>
      </div>
    </template>

    <!-- Pojedinačni izveštaj -->
    <template v-else>
      <div class="rm-detail-top">
        <div class="rm-detail-header">
          <button
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('report_mobile.back')"
            @click="closeScreen"
          >
            <v-icon size="24" color="#1c1917">mdi-chevron-left</v-icon>
          </button>
          <div class="rm-detail-header__text">
            <h1 class="rm-detail-header__title">{{ currentScreen.title }}</h1>
            <span class="rm-muted">{{ periodLong }}</span>
          </div>
          <button
            v-if="currentScreen.headerSearch"
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('search')"
            :aria-pressed="String(searchOpen)"
            @click="toggleSearch"
          >
            <v-icon size="22" color="#1c1917">mdi-magnify</v-icon>
          </button>
          <button
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('report_mobile.more_options')"
            @click="actionsSheet = true"
          >
            <v-icon size="22" color="#1c1917">mdi-dots-vertical</v-icon>
          </button>
        </div>

        <div v-if="currentScreen.headerSearch && searchOpen" class="rm-detail-search">
          <label class="rm-search">
            <v-icon size="18" color="#5f5852">mdi-magnify</v-icon>
            <span class="rm-visually-hidden">{{ $t("search") }}</span>
            <input ref="searchInput" v-model="search" type="search" :placeholder="$t('search')" />
          </label>
        </div>

        <div class="rm-chip-row">
          <button type="button" class="rm-chip" @click="periodSheet = true">
            <span class="rm-num">{{ periodShort }}</span>
            <v-icon size="16" color="#5f5852">mdi-chevron-down</v-icon>
          </button>
          <button
            v-if="currentScreen.companies"
            type="button"
            class="rm-chip"
            :class="{ 'rm-chip--accent': !allCompaniesSelected }"
            @click="filterSheet = true"
          >
            {{ companiesLabel }}
            <v-icon size="16" :color="allCompaniesSelected ? '#5f5852' : '#9a3412'">
              mdi-chevron-down
            </v-icon>
          </button>
          <button
            v-if="currentScreen.shifts"
            type="button"
            class="rm-chip"
            :class="{ 'rm-chip--accent': !allShiftsSelected }"
            @click="filterSheet = true"
          >
            {{ shiftsLabel }}
            <v-icon size="16" :color="allShiftsSelected ? '#5f5852' : '#9a3412'">
              mdi-chevron-down
            </v-icon>
          </button>
          <button
            v-if="currentScreen.companies || currentScreen.shifts"
            type="button"
            class="rm-chip"
            @click="filterSheet = true"
          >
            <v-icon size="18" color="#1c1917">mdi-tune-variant</v-icon>
            {{ $t("filters") }}
          </button>
        </div>
      </div>

      <component
        :is="currentScreen.component"
        v-if="!currentScreen.legacy"
        :key="`${currentScreenKey}-${reloadKey}`"
        v-bind="screenProps"
        :shifts.sync="shifts"
        @open-screen="openScreen"
      ></component>
      <div v-else class="rm-legacy">
        <component :is="currentScreen.component" :key="`${currentScreenKey}-${reloadKey}`"></component>
      </div>
    </template>

    <report-mobile-period-sheet
      v-model="periodSheet"
      :from="from"
      :to="to"
      :presets="presets"
      @apply="applyPeriod"
    ></report-mobile-period-sheet>

    <report-mobile-filter-sheet
      v-model="filterSheet"
      :from="from"
      :to="to"
      :presets="presets"
      :shifts="shifts"
      :show-shifts="Boolean(currentScreen && currentScreen.shifts)"
      :show-companies="Boolean(currentScreen && currentScreen.companies)"
      :companies="mealCompanies"
      :selected-company-ids="selectedCompanyIds"
      :views="views"
      :selected-view="selectedView"
      @open-period="openPeriodFromFilters"
      @apply="applyFilters"
    ></report-mobile-filter-sheet>

    <v-bottom-sheet v-model="actionsSheet" max-width="600">
      <section class="rm-theme rm-sheet" :aria-label="$t('report_mobile.more_options')">
        <div class="rm-sheet__body">
          <div class="rm-sheet__handle"></div>
          <ul class="rm-list rm-actions">
            <li>
              <button type="button" class="rm-actions__row" @click="runAction('print')">
                <v-icon size="22" color="#1c1917">mdi-printer-outline</v-icon>
                {{ $t("report_mobile.print_pdf") }}
              </button>
            </li>
            <li>
              <button type="button" class="rm-actions__row" @click="runAction('excel')">
                <v-icon size="22" color="#15803d">mdi-microsoft-excel</v-icon>
                {{ $t("report_mobile.download_excel") }}
              </button>
            </li>
            <li>
              <button type="button" class="rm-actions__row" @click="runAction('reload')">
                <v-icon size="22" color="#1c1917">mdi-refresh</v-icon>
                {{ $t("report_mobile.refresh") }}
              </button>
            </li>
          </ul>
        </div>
      </section>
    </v-bottom-sheet>
  </div>
</template>

<script>
import {
  ReportMealsTabFoods,
  ReportMealsTabGroceries,
  ReportMealsTabEmployees,
  ReportMealsTabOrders,
  ReportMealsTabRestaurants,
  ReportMealsTabNonRegistered,
  ReportMealsTabReservedMeals,
  ReportMealsTabTakenMeals,
} from "@/components/report/meals";
import ReportMobileMeals from "./screens/ReportMobileMeals.vue";
import ReportMobilePackages from "./screens/ReportMobilePackages.vue";
import ReportMobileWorkerOrders from "./screens/ReportMobileWorkerOrders.vue";
import ReportMobileTaken from "./screens/ReportMobileTaken.vue";
import ReportMobileAllSummary from "./screens/ReportMobileAllSummary.vue";
import ReportMobileAllPacking from "./screens/ReportMobileAllPacking.vue";
import ReportMobilePeriodSheet from "./ReportMobilePeriodSheet.vue";
import ReportMobileFilterSheet from "./ReportMobileFilterSheet.vue";
import hasPlural from "./hasPlural";
import { buildPeriodPresets, findPresetKey, formatRange, formatShortDate } from "./periods";

const ALL_SHIFTS = [1, 2, 3, 4];
const SCREEN_QUERY_KEY = "r";
const ALL_COMPANIES_VIEW = 4;

export default {
  name: "ReportMobile",
  mixins: [hasPlural],
  components: {
    ReportMobilePeriodSheet,
    ReportMobileFilterSheet,
  },
  props: {
    /** [from, to] u formatu YYYY-MM-DD */
    value: {
      type: Array,
      required: true,
    },
    selectedView: {
      type: Number,
      default: 1,
    },
    views: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    shifts: [...ALL_SHIFTS],
    periodSheet: false,
    filterSheet: false,
    actionsSheet: false,
    searchOpen: false,
    reloadKey: 0,
    pushedScreens: 0,
    pendingSearch: "",
  }),
  computed: {
    from() {
      return this.value[0];
    },
    to() {
      return this.value[1] || this.value[0];
    },
    presets() {
      return buildPeriodPresets();
    },
    quickPresets() {
      return this.presets.filter((preset) => preset.key !== "last_month");
    },
    activePresetKey() {
      return findPresetKey(this.presets, this.from, this.to);
    },
    activeQuickPreset() {
      return this.quickPresets.some((preset) => preset.key === this.activePresetKey);
    },
    periodShort() {
      if (this.activePresetKey) {
        return this.$t(`report_mobile.presets.${this.activePresetKey}`);
      }
      return formatRange(this.from, this.to);
    },
    periodLong() {
      if (this.from !== this.to) {
        return formatRange(this.from, this.to);
      }
      const weekday = this.localeDate(this.from, { weekday: "long" });
      const capitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1);
      return `${capitalized}, ${formatShortDate(this.from, true)}`;
    },
    restaurantName() {
      return this.toLatin(this.$store.state.restaurant?.name || "");
    },
    screens() {
      return {
        groceries: {
          title: this.$t("groceri"),
          subtitle: this.$t("report_mobile.hints.groceries"),
          icon: "mdi-leaf",
          component: ReportMealsTabGroceries,
          legacy: true,
        },
        foods: {
          title: this.$t("meals_main"),
          subtitle: this.$t("report_mobile.hints.foods"),
          icon: "mdi-pot-steam-outline",
          component: ReportMealsTabFoods,
          legacy: true,
        },
        meals: {
          title: this.$t("meals"),
          subtitle: this.$t("report_mobile.hints.meals"),
          icon: "mdi-bowl-mix-outline",
          component: ReportMobileMeals,
          companies: true,
          headerSearch: true,
        },
        workerOrders: {
          title: this.$t("report_mobile.worker_orders"),
          subtitle: this.$t("report_mobile.hints.worker_orders"),
          icon: "mdi-table",
          component: ReportMobileWorkerOrders,
          companies: true,
          shifts: true,
        },
        employees: {
          title: this.$t("employees"),
          subtitle: this.$t("report_mobile.hints.employees"),
          icon: "mdi-account-outline",
          component: ReportMealsTabEmployees,
          legacy: true,
        },
        reserved: {
          title: this.$t("report_mobile.reserves"),
          subtitle: this.$t("r_o"),
          icon: "mdi-archive-outline",
          component: ReportMealsTabReservedMeals,
          legacy: true,
        },
        packages: {
          title: this.$t("pak"),
          subtitle: this.$t("report_mobile.hints.packages"),
          icon: "mdi-shopping-outline",
          component: ReportMobilePackages,
          companies: true,
          shifts: true,
          headerSearch: true,
        },
        taken: {
          title: this.$t("taken"),
          subtitle: this.$t("report_mobile.hints.taken"),
          icon: "mdi-truck-outline",
          component: ReportMobileTaken,
          shifts: true,
          headerSearch: true,
        },
        takenMeals: {
          title: this.$t("t_o"),
          subtitle: this.$t("report_mobile.hints.taken_meals"),
          icon: "mdi-check-circle-outline",
          component: ReportMealsTabTakenMeals,
          legacy: true,
        },
        ordered: {
          title: this.$t("warehouse.ordered"),
          subtitle: this.$t("report_mobile.hints.ordered"),
          icon: "mdi-grid",
          component: ReportMealsTabOrders,
          legacy: true,
        },
        nonRegistered: {
          title: this.$t("un"),
          subtitle: this.$t("report_mobile.hints.non_registered"),
          icon: "mdi-account-question-outline",
          component: ReportMealsTabNonRegistered,
          legacy: true,
        },
        restaurants: {
          title: this.$t("restaurants"),
          subtitle: this.$t("report_mobile.hints.restaurants"),
          icon: "mdi-storefront-outline",
          component: ReportMealsTabRestaurants,
          legacy: true,
        },
        allMeals: {
          title: this.$t("meals"),
          subtitle: this.$t("report_mobile.hints.all_meals"),
          icon: "mdi-bowl-mix-outline",
          component: ReportMobileAllSummary,
          props: { mode: "meals" },
          headerSearch: true,
        },
        allCompanies: {
          title: this.$t("companies"),
          subtitle: this.$t("report_mobile.hints.all_companies"),
          icon: "mdi-domain",
          component: ReportMobileAllSummary,
          props: { mode: "companies" },
          headerSearch: true,
        },
        allPacking: {
          title: this.$t("pak"),
          subtitle: this.$t("report_mobile.hints.all_packing"),
          icon: "mdi-shopping-outline",
          component: ReportMobileAllPacking,
          shifts: true,
          headerSearch: true,
          bindSelectedShifts: true,
        },
      };
    },
    currentViewName() {
      return this.views.find((view) => view.id === this.selectedView)?.name || "";
    },
    /** Prikaz "Sve firme – zbirni izvještaj" ima svoje ekrane. */
    isAllCompaniesView() {
      return this.selectedView === ALL_COMPANIES_VIEW;
    },
    screenProps() {
      const screen = this.currentScreen || {};
      return {
        ...(screen.props || {}),
        ...(screen.bindSelectedShifts ? { selectedShifts: this.shifts } : {}),
      };
    },
    menuGroups() {
      const group = (key, title, keys) => ({
        key,
        title,
        items: keys.map((itemKey) => ({ key: itemKey, ...this.screens[itemKey] })),
      });
      if (this.isAllCompaniesView) {
        return [
          group("all", this.$t("all_companies_summary_report"), [
            "allMeals",
            "allCompanies",
            "allPacking",
          ]),
        ];
      }
      return [
        group("kitchen", this.$t("report_mobile.groups.kitchen"), [
          "groceries",
          "foods",
          "meals",
        ]),
        group("orders", this.$t("report_mobile.groups.orders"), [
          "workerOrders",
          "employees",
          "reserved",
        ]),
        group("handout", this.$t("report_mobile.groups.handout"), [
          "packages",
          "taken",
          "takenMeals",
        ]),
        group("other", this.$t("report_mobile.groups.other"), [
          "ordered",
          "nonRegistered",
          "restaurants",
        ]),
      ];
    },
    currentScreenKey() {
      const key = this.$route.query[SCREEN_QUERY_KEY];
      const allowed = this.menuGroups.some((group) => group.items.some((item) => item.key === key));
      return allowed ? key : null;
    },
    currentScreen() {
      return this.currentScreenKey ? this.screens[this.currentScreenKey] : null;
    },
    mealCompanies() {
      return this.$store.state.report.mealCompanies || [];
    },
    selectedCompanies() {
      return this.$store.state.report.selectedMealCompanies || [];
    },
    selectedCompanyIds() {
      return this.selectedCompanies.map((company) => company.id);
    },
    allCompaniesSelected() {
      return (
        this.mealCompanies.length > 0 &&
        this.selectedCompanies.length === this.mealCompanies.length
      );
    },
    companiesLabel() {
      if (this.selectedCompanies.length === 1) {
        return this.toLatin(this.selectedCompanies[0].name);
      }
      if (this.allCompaniesSelected) {
        return this.$t("report_mobile.all_companies_n", { n: this.mealCompanies.length });
      }
      return this.pluralT("report_mobile.companies_n", this.selectedCompanies.length);
    },
    allShiftsSelected() {
      return this.shifts.length === ALL_SHIFTS.length;
    },
    shiftsLabel() {
      if (this.allShiftsSelected) {
        return this.$t("report_mobile.all_shifts");
      }
      if (this.shifts.length === 1) {
        return this.$t("report_mobile.shift_n", { n: this.shifts[0] });
      }
      return this.$t("report_mobile.shifts_list", { list: this.shifts.join(", ") });
    },
    search: {
      get() {
        return this.$store.state.report.filters.search;
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "search", value });
      },
    },
  },
  watch: {
    currentScreenKey(newKey, oldKey) {
      if (newKey === oldKey) {
        return;
      }
      this.search = this.pendingSearch;
      this.pendingSearch = "";
      this.searchOpen = Boolean(this.search) && Boolean(this.currentScreen?.headerSearch);
      window.scrollTo(0, 0);
    },
  },
  created() {
    // Store pamti period iz prethodne posete; ekran uvek kreće od perioda koji prikazuje.
    this.$store.commit("report/SET_DATE_RANGE", { from: this.from, to: this.to });
    this.ensureCompanies();
  },
  beforeDestroy() {
    this.search = "";
  },
  methods: {
    async ensureCompanies() {
      if (!this.mealCompanies.length) {
        await this.$store.dispatch("report/fetchMealCompanies");
      }
      if (!this.selectedCompanies.length && this.mealCompanies.length) {
        this.$store.commit("report/SET_SELECTED_MEAL_COMPANIES", [...this.mealCompanies]);
      }
    },
    presetChipLabel(preset) {
      const label = this.$t(`report_mobile.presets.${preset.key}`);
      const isSingleDay = preset.from === preset.to;
      if (this.activePresetKey === preset.key && isSingleDay) {
        return `${label} · ${formatShortDate(preset.from)}`;
      }
      return label;
    },
    applyPeriod({ from, to }) {
      this.$emit("input", [from, to]);
    },
    openPeriodFromFilters() {
      this.filterSheet = false;
      this.periodSheet = true;
    },
    applyFilters({ from, to, shifts, companyIds, view }) {
      if (from !== this.from || to !== this.to) {
        this.applyPeriod({ from, to });
      }
      this.shifts = shifts;
      if (companyIds.join(",") !== this.selectedCompanyIds.join(",")) {
        this.$store.commit(
          "report/SET_SELECTED_MEAL_COMPANIES",
          this.mealCompanies.filter((company) => companyIds.includes(company.id))
        );
      }
      if (view !== this.selectedView) {
        this.$emit("update:selectedView", view);
      }
    },
    openScreen(key, { search = "" } = {}) {
      if (key === this.currentScreenKey) {
        this.search = search;
        return;
      }
      this.pendingSearch = search;
      this.pushedScreens += 1;
      this.$router
        .push({ query: { ...this.$route.query, [SCREEN_QUERY_KEY]: key } })
        .catch(() => {});
    },
    closeScreen() {
      // Ekran otvoren sa ove stranice → nazad kroz istoriju (radi i hardverski "nazad");
      // direktan link (?r=...) → zameni URL, da ne izađemo sa /izvestaj.
      if (this.pushedScreens > 0) {
        this.pushedScreens -= 1;
        this.$router.back();
        return;
      }
      const query = { ...this.$route.query };
      delete query[SCREEN_QUERY_KEY];
      this.$router.replace({ query }).catch(() => {});
    },
    toggleSearch() {
      this.searchOpen = !this.searchOpen;
      if (this.searchOpen) {
        this.$nextTick(() => this.$refs.searchInput?.focus());
      } else {
        this.search = "";
      }
    },
    runAction(action) {
      this.actionsSheet = false;
      if (action === "print") {
        this.$store.commit("SET_PRINTING");
      } else if (action === "excel") {
        this.$store.commit("SET_EXCEL");
      } else if (action === "reload") {
        this.reloadKey += 1;
      }
    },
  },
};
</script>

<style lang="scss">
@import "./reportMobile.scss";

.rm {
  background: var(--rm-bg);
  min-height: calc(100vh - 64px);
  padding-bottom: 8px;
}

.rm-home-header {
  display: flex;
  flex-direction: column;
  padding: 16px 20px 8px;

  &__title {
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.2;
  }
}

.rm-theme .rm-view-switch {
  align-self: flex-start;
  margin-top: 6px;
  height: 32px;
  padding: 0 10px 0 12px;
  border: 1px solid var(--rm-accent-border);
  border-radius: 16px;
  background: var(--rm-accent-soft);
  color: var(--rm-accent-strong);
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.rm-home-periods {
  padding-bottom: 16px;
}

.rm-home-groups {
  padding: 0 16px 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.rm-home-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rm-theme .rm-menu-row {
  width: 100%;
  min-height: 64px;
  padding: 12px 14px;
  border: none;
  border-bottom: 1px solid var(--rm-divider);
  background: transparent;
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;
  color: var(--rm-text);

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: var(--rm-bg);
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: var(--rm-accent-soft);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
  }
}

.rm-detail-top {
  position: sticky;
  top: 64px;
  z-index: 3;
  background: var(--rm-bg);
}

.rm-detail-header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 8px 4px;

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.rm-detail-search {
  padding: 4px 16px;
}

.rm-legacy {
  margin: 8px 8px 0;
  padding: 4px 8px 16px;
  background: var(--rm-surface);
  border: 1px solid var(--rm-border);
  border-radius: 18px;
}

.rm-actions {
  display: flex;
  flex-direction: column;
}

.rm-theme .rm-actions__row {
  width: 100%;
  min-height: 56px;
  border: none;
  border-bottom: 1px solid var(--rm-divider);
  background: transparent;
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 16px;
  font-weight: 600;
  text-align: left;
}

.rm-actions li:last-child .rm-actions__row {
  border-bottom: none;
}
</style>
